/**
 * A CUSTOMER, IN A DRAWER — the quick look, without leaving the list.
 *
 * Tap somebody in the list and a drawer slides in with who they are, their
 * cars and their work, while the list keeps its place behind it. The full
 * page is one tap away for the deep stuff; this is for the nine times in
 * ten you only wanted to glance, ring them, or start the next job.
 *
 * The header is the person: their initials, their name, and the three
 * things you reach for first — call, copy the number, email. Under it a
 * strip of the numbers that matter (open work, cars, what they have paid),
 * then the cars and the work themselves. Start a quote or a job and the
 * same drawer becomes a short wizard, the WeRV new-trip flow ported here.
 */

import { db } from "../data/client.ts";
import { esc, money, firstSentence } from "../ui/dom.ts";
import { STATUS_LABEL, type Status } from "../data/jobs.ts";
import { shopWords } from "../ui/words.ts";
import { cap } from "../domain/trade.ts";
import { getCustomer, type CustomerWhole } from "../data/customers.ts";
import { openDrawer, paintDrawer, closeDrawer, drawerFail } from "../ui/drawer.ts";

type Kind = "quote" | "job";
const KINDWORD: Record<Kind, { title: string; go: string }> = {
  quote: { title: "New quote", go: "Start the quote" },
  job: { title: "New job", go: "Start it" },
};

const initials = (n: string) =>
  n.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase() || "?";
const carName = (v: CustomerWhole["vehicles"][number]): string =>
  [v.year, v.make, v.model].filter(Boolean).join(" ") || v.description || cap(shopWords().thing);
const isOpen = (s: string) => s !== "paid" && s !== "closed" && s !== "cancelled";

const ICON = {
  phone: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6.5 3.5 9 4l1 3.5-1.8 1.4a12 12 0 0 0 5 5L14.5 16l3.5 1 .5 2.5a1.5 1.5 0 0 1-1.6 1.5A15.5 15.5 0 0 1 3 5.6 1.5 1.5 0 0 1 4.5 4z"/></svg>`,
  copy: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h8"/></svg>`,
  mail: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>`,
  car: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M5 11l1.5-4A2 2 0 0 1 8.4 5.6h7.2a2 2 0 0 1 1.9 1.4L19 11m-14 0h14m-14 0a2 2 0 0 0-2 2v3h2m14-5a2 2 0 0 1 2 2v3h-2M6 16h12M6 16v1.5M18 16v1.5"/><circle cx="7.5" cy="14" r=".6"/><circle cx="16.5" cy="14" r=".6"/></svg>`,
};

const digitsTel = (p: string | null | undefined) => String(p ?? "").replace(/[^\d+]/g, "");

/**
 * KEEPING THE CHOSEN OPTION LOOKING CHOSEN.
 *
 * The highlight used to be `.wiz-opt:has(input:checked)` in CSS. The build
 * targets safari15, where `:has()` does not match and does not complain,
 * so on the shop's iPad the option you picked looked identical to the ones
 * you did not — in a wizard whose entire job is telling you where you are.
 *
 * ONE LISTENER ON THE DOCUMENT, for the same reason bindEditables uses
 * one: the drawer repaints itself on every step, and a listener bound to
 * the markup would have to be re-added each time and would one day not be.
 * This survives every repaint because it is never attached to any of it.
 *
 * The initial state is rendered with the markup — the template already
 * knows which radio carries `checked` — so the first paint is right before
 * anybody touches anything.
 */
let wizWired = false;
function wireWizardPicks(): void {
  if (wizWired) return;
  wizWired = true;
  document.addEventListener("change", (e) => {
    const input = e.target as HTMLInputElement | null;
    if (!input || input.type !== "radio" || !input.closest(".wiz-opt")) return;
    // Every option sharing this radio's name, so the one being turned OFF
    // loses the class in the same breath as the new one gains it.
    const scope = input.closest(".sheet-body") ?? document;
    scope.querySelectorAll<HTMLElement>(`.wiz-opt`).forEach((label) => {
      const radio = label.querySelector<HTMLInputElement>("input[type=radio]");
      if (radio && radio.name === input.name) label.classList.toggle("on", radio.checked);
    });
  });
}

/** Open the customer drawer. Everything after repaints this same drawer. */
export async function openCustomerDrawer(id: string): Promise<void> {
  wireWizardPicks();
  openDrawer({ body: `<div class="drw-loading">Reading the record…</div>`, label: "Customer" });
  let whole: CustomerWhole | null;
  try {
    whole = await getCustomer(id);
  } catch (e) {
    paintDrawer(`<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`);
    return;
  }
  if (!whole) { paintDrawer(`<div class="drw-loading">That customer is not here.</div>`); return; }
  paintCustomer(whole);
}

function paintCustomer(whole: CustomerWhole): void {
  const c = whole.customer;
  const openJobs = whole.jobs.filter((j) => isOpen(j.status));
  const billed = whole.invoices.filter((i) => i.status === "paid")
    .reduce((n, i) => n + Number(i.total || 0), 0);
  const since = c.created_at ? new Date(c.created_at).getFullYear() : null;

  const quick = [
    c.phone && `<a class="qk" href="tel:${esc(digitsTel(c.phone))}"><span class="ico">${ICON.phone}</span>Call</a>`,
    c.phone && `<button class="qk" id="drw-copy" type="button"><span class="ico">${ICON.copy}</span><span class="lbl">Copy</span></button>`,
    c.email && `<a class="qk" href="mailto:${esc(c.email)}"><span class="ico">${ICON.mail}</span>Email</a>`,
  ].filter(Boolean).join("");

  const carsRows = whole.vehicles.length
    ? whole.vehicles.map((v) => `<div class="cust-row">
        <span class="cust-row-ico">${ICON.car}</span>
        <b>${esc(carName(v))}</b>
        ${v.license_plate ? `<span class="cust-row-side mono">${esc(v.license_plate)}</span>` : ""}
      </div>`).join("")
    : `<div class="cust-empty">No ${esc(shopWords().things)} on file yet.</div>`;

  const shown = whole.jobs.slice(0, 6);
  const workRows = whole.jobs.length
    ? shown.map((j) => `<a class="cust-row cust-row-link" href="#/job/${esc(j.id)}">
        <b>#${esc(j.job_number)} <span class="cust-row-title">${esc(j.title ?? "Job")}</span></b>
        <span class="pill s-${esc(j.status)} cust-row-side">${esc(STATUS_LABEL[j.status as Status] ?? j.status)}</span>
      </a>`).join("")
      + (whole.jobs.length > shown.length
        ? `<a class="cust-more" href="#/customer/${esc(c.id)}">See all ${whole.jobs.length} →</a>` : "")
    : `<div class="cust-empty">Nothing on the books yet.</div>`;

  paintDrawer(`
    <header class="cust-hero">
      <span class="avatar big cust-av">${esc(initials(c.full_name))}</span>
      <div class="cust-hero-main">
        <h2>${esc(c.full_name)}</h2>
        <div class="quiet">${[since ? `On file since ${since}` : "", c.source ? esc(c.source) : ""]
          .filter(Boolean).join(" · ") || "Customer"}</div>
      </div>
    </header>

    ${quick ? `<div class="cust-quick">${quick}</div>` : ""}

    <div class="cust-stats">
      <div class="cust-stat"><b>${openJobs.length}</b><span>Open</span></div>
      <div class="cust-stat"><b>${whole.vehicles.length}</b><span>${esc(cap(shopWords().things))}</span></div>
      <div class="cust-stat"><b>${billed > 0 ? esc(money(billed)) : "—"}</b><span>Billed</span></div>
    </div>

    <section class="cust-sec">
      <div class="cust-sec-h">${esc(cap(shopWords().things))}</div>
      ${carsRows}
    </section>

    <section class="cust-sec">
      <div class="cust-sec-h">Work ${openJobs.length ? `<span class="count">${openJobs.length} open</span>` : ""}</div>
      ${workRows}
    </section>

    ${c.notes ? `<section class="cust-sec"><div class="cust-sec-h">Notes</div>
      <p class="cust-notes">${esc(c.notes)}</p></section>` : ""}
  `, {
    acts: `
      <button class="btn primary" id="drw-quote">New quote</button>
      <button class="btn" id="drw-job">New job</button>
      <a class="btn ghost" href="#/customer/${esc(c.id)}" id="drw-full">Full page</a>`,
  });

  document.getElementById("drw-quote")?.addEventListener("click", () => wizard(whole, "quote"));
  document.getElementById("drw-job")?.addEventListener("click", () => wizard(whole, "job"));
  document.getElementById("drw-full")?.addEventListener("click", () => closeDrawer());
  document.getElementById("drw-copy")?.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(c.phone ?? "");
      const lbl = document.querySelector<HTMLElement>("#drw-copy .lbl");
      if (lbl) { lbl.textContent = "Copied"; setTimeout(() => { lbl.textContent = "Copy"; }, 1400); }
    } catch { /* clipboard blocked; the number is on screen anyway */ }
  });
}

/* ── the wizard ─────────────────────────────────────────────────────── */

interface Wiz { kind: Kind; step: number; want: string; vehicleId: string | null;
  newCar: string; busy: boolean; }
/* "Car" is the only one of these that is not the same word in every
   shop. The rail is redrawn on each step, so reading it at call time
   is enough. See src/domain/trade.ts. */
const steps = (): string[] => ["What", cap(shopWords().thing), "Confirm"];

function rail(step: number): string {
  return `<ol class="wiz-rail">${steps().map((s, i) =>
    `<li class="${i === step ? "now" : i < step ? "done" : "next"}"><span class="dot"></span>${esc(s)}</li>`).join("")}</ol>`;
}

function wizard(whole: CustomerWhole, kind: Kind): void {
  const w: Wiz = { kind, step: 0, want: "", vehicleId: whole.vehicles[0]?.id ?? null, newCar: "", busy: false };

  const backBtn = w.step > 0
    ? `<button class="btn ghost" id="wz-back">Back</button>`
    : `<button class="btn ghost" id="wz-cancel">Back</button>`;

  function head(sub: string): string {
    return `${rail(w.step)}
      <div class="wiz-head"><span class="avatar cust-av">${esc(initials(whole.customer.full_name))}</span>
        <div><div class="wiz-kind">${esc(KINDWORD[w.kind].title)}</div>
          <div class="quiet">${esc(sub)}</div></div></div>`;
  }

  function stepWhat(): void {
    paintDrawer(`${head("For " + whole.customer.full_name)}
      <label class="field"><span>What do they want?</span>
        <textarea id="wz-want" rows="3" placeholder="Brakes grinding at the front, started last week">${esc(w.want)}</textarea></label>
      <p class="wiz-hint">A line is enough — the ${esc(shopWords().thing)} and the pricing come next, and on the job card.</p>`, {
      acts: `${backBtn}<button class="btn primary" id="wz-next">Next</button>`,
    });
    wireBack();
    document.getElementById("wz-next")?.addEventListener("click", () => {
      w.want = (document.getElementById("wz-want") as HTMLTextAreaElement)?.value.trim() ?? "";
      if (!w.want) return drawerFail("Say what they want, even roughly.");
      w.step = 1; paint();
    });
    (document.getElementById("wz-want") as HTMLTextAreaElement | null)?.focus();
  }

  function stepCar(): void {
    const pick = whole.vehicles.map((v) =>
      `<label class="wiz-opt ${w.vehicleId === v.id ? "on" : ""}"><input type="radio" name="wz-car" value="${esc(v.id)}"
        ${w.vehicleId === v.id ? "checked" : ""}><span class="wiz-opt-ico">${ICON.car}</span>
        <span>${esc(carName(v))}</span></label>`).join("");
    paintDrawer(`${head(`Which ${shopWords().thing} is this for?`)}
      ${pick}
      <label class="wiz-opt ${w.vehicleId === null && !w.newCar ? "on" : ""}"><input type="radio" name="wz-car" value=""
        ${w.vehicleId === null && !w.newCar ? "checked" : ""}><span class="wiz-opt-ico">${ICON.car}</span>
        <span>No ${esc(shopWords().thing)} yet</span></label>
      <label class="field wiz-addcar"><span>Or add one <i>— optional</i></span>
        <input id="wz-newcar" placeholder="${esc(shopWords().example)}" value="${esc(w.newCar)}"></label>`, {
      acts: `${backBtn}<button class="btn primary" id="wz-next">Next</button>`,
    });
    wireBack();
    document.getElementById("wz-next")?.addEventListener("click", () => {
      const sel = (document.querySelector('input[name="wz-car"]:checked') as HTMLInputElement | null)?.value ?? "";
      w.newCar = (document.getElementById("wz-newcar") as HTMLInputElement)?.value.trim() ?? "";
      w.vehicleId = sel || null;
      w.step = 2; paint();
    });
  }

  function stepConfirm(): void {
    const car = w.newCar
      ? `${esc(w.newCar)} <span class="quiet">(new)</span>`
      : w.vehicleId
        ? esc(carName(whole.vehicles.find((v) => v.id === w.vehicleId)!))
        : `<span class="quiet">No ${esc(shopWords().thing)}</span>`;
    paintDrawer(`${head("Look right?")}
      <div class="wiz-review">
        <div class="wiz-rev-row"><span>Customer</span><b>${esc(whole.customer.full_name)}</b></div>
        <div class="wiz-rev-row"><span>${w.kind === "quote" ? "Quoting" : "Job"}</span><b>${esc(w.want)}</b></div>
        <div class="wiz-rev-row"><span>${esc(cap(shopWords().thing))}</span><b>${car}</b></div>
      </div>
      <p class="wiz-hint">Opens ${w.kind === "quote" ? "a quote" : "a job"} at estimate. Nothing is sent or booked.</p>`, {
      acts: `${backBtn}<button class="btn primary" id="wz-go">${esc(KINDWORD[w.kind].go)}</button>`,
    });
    wireBack();
    document.getElementById("wz-go")?.addEventListener("click", create);
  }

  function wireBack(): void {
    document.getElementById("wz-back")?.addEventListener("click", () => { w.step -= 1; paint(); });
    document.getElementById("wz-cancel")?.addEventListener("click", () => paintCustomer(whole));
  }

  async function create(): Promise<void> {
    if (w.busy) return;
    w.busy = true;
    const b = document.getElementById("wz-go") as HTMLButtonElement | null;
    if (b) { b.disabled = true; b.textContent = "Saving…"; }
    try {
      const sb = await db();
      const { data: me } = await sb.auth.getUser();
      const { data: prof } = await sb.from("profiles")
        .select("account_id").eq("id", me.user?.id ?? "").maybeSingle();
      const accountId = (prof as { account_id: string } | null)?.account_id;
      if (!accountId) throw new Error("Cannot tell which shop you belong to. Sign in again.");

      let vehicleId = w.vehicleId;
      if (!vehicleId && w.newCar) {
        const bits = w.newCar.split(/\s+/);
        const year = Number(bits[0]);
        const { data: v } = await sb.from("vehicles").insert({
          account_id: accountId, customer_id: whole.customer.id,
          year: year >= 1900 && year <= 2100 ? year : null,
          make: (year ? bits[1] : bits[0]) ?? null,
          model: (year ? bits.slice(2) : bits.slice(1)).join(" ") || null,
        }).select("id").single();
        vehicleId = (v as { id: string } | null)?.id ?? null;
      }

      const { data: job, error } = await sb.from("jobs").insert({
        account_id: accountId, customer_id: whole.customer.id, vehicle_id: vehicleId,
        status: "estimate",
        title: firstSentence(w.want).slice(0, 90) || (w.kind === "quote" ? "New quote" : "New job"),
        complaint: w.want || null,
        created_by: me.user?.id ?? null,
      }).select("id,job_number").single();
      if (error) throw new Error(error.message);
      const j = job as { id: string; job_number: string };
      finished(j.id, j.job_number);
    } catch (e) {
      w.busy = false;
      if (b) { b.disabled = false; b.textContent = KINDWORD[w.kind].go; }
      drawerFail(e instanceof Error ? e.message : String(e));
    }
  }

  function finished(jobId: string, jobNumber: string): void {
    paintDrawer(`
      <div class="wiz-done">
        <div class="wiz-done-mark">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
            stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 4.5 4.5L19 7"/></svg>
        </div>
        <h2>#${esc(jobNumber)} is open</h2>
        <p class="quiet">${esc(KINDWORD[w.kind].title)} for ${esc(whole.customer.full_name)}. Add the ${esc(shopWords().thing)}'s
          details, price the work and send it from the job card.</p>
      </div>`, {
      acts: `<a class="btn primary" href="#/job/${esc(jobId)}" id="wz-open">Open the job</a>
        <button class="btn ghost" id="wz-close">Close</button>`,
    });
    document.getElementById("wz-open")?.addEventListener("click", () => closeDrawer());
    document.getElementById("wz-close")?.addEventListener("click", () => closeDrawer());
  }

  function paint(): void { [stepWhat, stepCar, stepConfirm][w.step]!(); }
  paint();
}
